/**
 * OfferFormFields - The investor's offer fields, each with its error message.
 *
 * Renders the fields as a fragment so they sit directly in the caller's form
 * layout; the caller owns the form element, the submit button and submission.
 * The price and earnest money inputs are wrapped for redaction here, so every
 * caller masks them.
 */

import type { ReactNode } from 'react'
import type { Control, FieldErrors } from 'react-hook-form'
import { Redact } from '@/common/analytics'
import { Input } from '@/common/components/input'
import { Select } from '@/common/components/select'
import { Textarea } from '@/common/components/textarea'
import type { OfferFormData } from './offerFormSchema'
import * as markStyles from '../DealViabilityInsight/DealViabilityMark.css'
import * as styles from './OfferFormFields.css'

const CLOSING_TIMELINE_OPTIONS = [
  { value: '10_days', label: '10 days' },
  { value: '14_days', label: '14 days' },
  { value: '30_days', label: '30 days' },
  { value: 'flexible', label: 'Flexible' },
]

export interface OfferFormFieldsProps {
  control: Control<OfferFormData>
  errors: FieldErrors<OfferFormData>
  /** Disables every field, for example while the caller's submission is in flight. */
  disabled?: boolean
  /**
   * Rendered over the right edge of the offer amount field, for example the deal
   * viability mark. The field's wrapper anchors it when present.
   */
  priceMark?: ReactNode
  /** Rendered directly under the offer amount and its error, before the deposit field. */
  afterPrice?: ReactNode
}

export function OfferFormFields({
  control,
  errors,
  disabled = false,
  priceMark,
  afterPrice,
}: OfferFormFieldsProps) {
  return (
    <>
      {/* Offer Amount — offer price is PII, mask the input. */}
      <Redact as="div" className={priceMark ? markStyles.anchor : undefined}>
        <Input
          label="Offer amount"
          name="offer_price"
          control={control}
          mask="currency"
          prefix="$"
          error={!!errors.offer_price}
          disabled={disabled}
        />
        {priceMark}
      </Redact>
      {errors.offer_price && (
        <span className={styles.errorMessage}>{errors.offer_price.message}</span>
      )}
      {afterPrice}

      {/* Earnest Money Deposit — EMD is PII, mask the input. */}
      <Redact as="div">
        <Input
          label="Earnest money deposit"
          name="earnest_money_deposit"
          control={control}
          mask="percentage"
          suffix="%"
          error={!!errors.earnest_money_deposit}
          disabled={disabled}
          optional
        />
      </Redact>
      {errors.earnest_money_deposit && (
        <span className={styles.errorMessage}>{errors.earnest_money_deposit.message}</span>
      )}

      {/* Closing Timeline */}
      <Select
        label="Closing timeline"
        name="closing_timeline"
        control={control}
        options={CLOSING_TIMELINE_OPTIONS}
        placeholder="Select timeline"
        error={!!errors.closing_timeline}
        disabled={disabled}
        optional
      />
      {errors.closing_timeline && (
        <span className={styles.errorMessage}>{errors.closing_timeline.message}</span>
      )}

      {/* Notes */}
      <Textarea
        label="Notes"
        name="notes"
        control={control}
        placeholder="E.g., Cash offer, inspection contingency..."
        rows={3}
        maxLength={5000}
        optional
        error={!!errors.notes}
        disabled={disabled}
      />
      {errors.notes && (
        <span className={styles.errorMessage}>{errors.notes.message}</span>
      )}

      {/* Private Notes */}
      <Textarea
        label="Private notes"
        name="private_notes"
        control={control}
        placeholder="Notes only visible to you (not the agent)"
        rows={3}
        maxLength={5000}
        optional
        error={!!errors.private_notes}
        disabled={disabled}
      />
      {errors.private_notes && (
        <span className={styles.errorMessage}>{errors.private_notes.message}</span>
      )}
    </>
  )
}
